import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { ComponentProps, useEffect, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectIsLookingUpFromState,
  selectIsVerifyingUserFromState,
  selectLookupErrorDetailMapFromState,
  selectLookupTopLevelErrorFromState,
  selectVerificationEmailResentFromState,
  selectVerifyEmailTopLevelErrorFromState,
} from 'src/app/CspSignin/redux/cspSignin.selectors'
import {
  selectIsSigningInFromState,
  selectSigninErrorDetailMapFromState,
  selectSigninTopLevelErrorFromState,
  selectVerificationPhoneLoadingFromState,
} from 'src/app/Signin/redux/signin.selectors'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import ExtraVerificationPopup from 'src/components/ExtraVerificationPopup/ExtraVerificationPopup'
import SigninForm from 'src/components/SigninForm/SigninForm'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import UnauthedPageContainer from 'src/components/UnauthedPageContainer/UnauthedPageContainer'
import VerifyForm from 'src/components/VerifyForm/VerifyForm'
import { Environments } from 'src/configurations'
import {
  AuthTypes,
  CognitoErrorMessages,
  MfaDeliveryMedium,
  SupportedBrands,
  UserStatus,
} from 'src/enums'
import SigninCMS from 'src/feature/MarketingCMS/SigninCMS'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { Rdrct } from 'src/navigation/Lnk'
import { cspSigninUrl } from 'src/navigation/urls'
import { createSetIsExtraVerificationPopupOpen } from 'src/redux/actions/app.actions'
import {
  confirmCognitoSignin,
  createAbandonMfaChallengeAction,
  createClearAuthFlowTypeAction,
  createSetEmail,
  createSetUserStatus,
  extendSignIn,
  postAuthType,
  postSendPhoneVerificationCode,
  postUserResendVerification,
  postUserVerificationThenPostSignin,
} from 'src/redux/actions/auth.actions'
import { selectIsExtraVerificationPopupOpenFromState } from 'src/redux/selectors/app.selectors'
import {
  selectAuthTypeFromState,
  selectDidSessionExpireAutomaticallyFromState,
  selectEmailFromState,
  selectFetchAuthTypeTopLevelErrorFromState,
  selectIsCspAuthenticatedFromState,
  selectMfaChallengeInitiated,
  selectMfaDeliveryMediumFromState,
  selectMfaDestinationFromState,
  selectNullSafeLocaleFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectUserEmailVerificationStatusFromState,
  selectUserStatusFromState,
  selectVerificationPhoneResentFromState,
  selectVerifyPhoneTopLevelErrorFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandName,
  selectEnvironmentFromState,
} from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

import style, { boxStyling, halfPageStyling } from './SigninPage.style'

const PageAndBoxContainer: React.FC<
  {
    showVerifyForm: boolean
    brandName: SupportedBrands
    showVerifyPhoneForm: boolean
  } & ComponentProps<'div'>
> = ({ showVerifyForm, brandName, showVerifyPhoneForm, children }) => {
  return (
    <UnauthedPageContainer
      topLeftImage={
        showVerifyForm && brandName !== SupportedBrands.Bmo ? (
          <div
            css={css`
              position: relative;
              top: 30px;
              left: 30px;
            `}
          >
            <BrandPrimaryLogo />
          </div>
        ) : undefined
      }
    >
      <UnauthedBoxContainer
        className={'unauthedBoxContainer'}
        hideBrandLogo={showVerifyForm}
        hideFooter={showVerifyForm || showVerifyPhoneForm}
        noBox={showVerifyForm || showVerifyPhoneForm}
      >
        {children}
      </UnauthedBoxContainer>
    </UnauthedPageContainer>
  )
}

const SigninPage: React.FC = () => {
  const email = useSelector(selectEmailFromState)
  const userStatus = useSelector(selectUserStatusFromState)
  const isLookingUp = useSelector(selectIsLookingUpFromState)
  const isSigningIn = useSelector(selectIsSigningInFromState)
  const isVerifyingUser = useSelector(selectIsVerifyingUserFromState)
  const verificationEmailResent = useSelector(
    selectVerificationEmailResentFromState,
  )
  const resendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )
  const verifyEmailTopLevelError = useSelector(
    selectVerifyEmailTopLevelErrorFromState,
  )
  const verificationPhoneResent = useSelector(
    selectVerificationPhoneResentFromState,
  )
  const userEmailVerificationStatus = useSelector(
    selectUserEmailVerificationStatusFromState,
  )
  const verifyPhoneTopLevelError = useSelector(
    selectVerifyPhoneTopLevelErrorFromState,
  )
  const verifyPhoneLoading = useSelector(
    selectVerificationPhoneLoadingFromState,
  )
  const lookupTopLevelError = useSelector(selectLookupTopLevelErrorFromState)
  const lookupErrorDetailMap = useSelector(selectLookupErrorDetailMapFromState)
  const signinTopLevelError = useSelector(selectSigninTopLevelErrorFromState)
  const signinErrorDetailMap = useSelector(selectSigninErrorDetailMapFromState)
  const fetchAuthTypeTopLevelError = useSelector(
    selectFetchAuthTypeTopLevelErrorFromState,
  )
  const didSessionExpireAutomatically = useSelector(
    selectDidSessionExpireAutomaticallyFromState,
  )
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const env = useSelector(selectEnvironmentFromState)
  const mfaChallengeInitiated = useSelector(selectMfaChallengeInitiated)
  const authType = useSelector(selectAuthTypeFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useSelector(selectLocationFromState)
  const brandName = useSelector(selectBrandName)
  const isExtraVerificationPopupOpen = useSelector(
    selectIsExtraVerificationPopupOpenFromState,
  )
  const mfaDestination = useSelector(selectMfaDestinationFromState)
  const mfaDeliveryMedium = useSelector(selectMfaDeliveryMediumFromState)

  const { analytics } = useAnalyticsContext()
  const [userEmailVerified, setUserEmailVerified] = useState<boolean>(false)
  const dispatch = useBackgroundDispatch()

  useEffect((): void => {
    dispatch(createClearAuthFlowTypeAction())
  }, [])

  const dispatchSignin = (
    email: string,
    password?: string,
    newCode?: boolean,
  ): void => {
    dispatch(extendSignIn(email, password, newCode, analytics))
  }

  const dispatchSetEmail = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const dispatchSetUserStatus = (val?: UserStatus): void => {
    dispatch(createSetUserStatus(val))
  }

  const dispatchPostUserResendVerification = (val: string): void => {
    dispatch(postUserResendVerification(val))
  }

  const dispatchPostUserVerificationThenSignin = (code: string): void => {
    if (!authType) {
      dispatch(
        postAuthType({
          data: email,
        }),
      )
    }
    dispatch(
      postUserVerificationThenPostSignin(
        code,
        email,
        password,
        acceptOrgInvite,
      ),
    )
  }

  const dispatchConfirmCognitoSignin = (
    code: string,
    rememberDevice?: boolean,
  ): void => {
    dispatch(confirmCognitoSignin(code, rememberDevice))
  }

  const dispatchAbandonMfaChallenge = (): void => {
    dispatch(createAbandonMfaChallengeAction())
  }

  const [password, setPassword] = useState<string>(
    location.state?.password ?? '',
  )

  const acceptOrgInvite = !!location.state?.acceptOrgInvite

  const signinFormTopLevelError = (): string | undefined => {
    if (fetchAuthTypeTopLevelError) {
      return fetchAuthTypeTopLevelError
    }
    if (lookupTopLevelError || signinTopLevelError) {
      if (authType === AuthTypes.cognito) {
        switch (signinTopLevelError) {
          case CognitoErrorMessages.incorrectUsernameOrPassword:
            return translateSingleString(
              locale,
              'Unable to log in, please review your email/password',
            )
          case CognitoErrorMessages.passwordResetRequiredForTheUser:
            return translateSingleString(
              locale,
              "For security purposes, your password needs to be reset. Please click the 'Forgot Your Password?' link below to update your credentials.",
            )
          default:
            return (
              lookupTopLevelError ||
              (signinTopLevelError !== UserStatus.PhoneNotVerified
                ? signinTopLevelError
                : undefined)
            )
        }
      } else {
        return (
          lookupTopLevelError ||
          (signinTopLevelError !== UserStatus.PhoneNotVerified
            ? signinTopLevelError
            : undefined)
        )
      }
    } else {
      return undefined
    }
  }

  const showVerifyForm =
    !isLookingUp &&
    !isSigningIn &&
    signinTopLevelError === UserStatus.NotVerified
  const showVerifyPhoneForm =
    !isLookingUp &&
    !isSigningIn &&
    signinTopLevelError === UserStatus.PhoneNotVerified

  useEffect((): void => {
    if (userEmailVerificationStatus === UserStatus.EmailVerified) {
      setUserEmailVerified(true)
    } else {
      setUserEmailVerified(false)
    }
  }, [userEmailVerificationStatus])

  useEffect((): void => {
    if (showVerifyForm) {
      dispatchPostUserResendVerification(email)
    }
  }, [showVerifyForm])

  useEffect((): void => {
    if (showVerifyPhoneForm && !userEmailVerified) {
      dispatch(postSendPhoneVerificationCode(email))
      dispatch(createSetIsExtraVerificationPopupOpen(true))
    }
  }, [showVerifyPhoneForm, showVerifyForm, userEmailVerified])

  const appName = useSelector(selectAppName)
  const brand = useSelector(selectBrandName)
  const showMarketingCMS = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_marketing_cms

  const signInContent =
    env === Environments.Prod ? (
      <GoogleReCaptchaProvider
        reCaptchaKey={'6Lda69MgAAAAALa0nw9lUWo7NBrvsQzwwAvwEAuL'}
      >
        <SigninForm
          className={'signinForm'}
          email={email}
          setEmail={dispatchSetEmail}
          password={password}
          setPassword={setPassword}
          postSignin={dispatchSignin}
          isSigningIn={isLookingUp || isSigningIn}
          errorDetailMap={lookupErrorDetailMap || signinErrorDetailMap}
          topLevelError={signinFormTopLevelError()}
          didSessionExpireAutomatically={didSessionExpireAutomatically}
        />
      </GoogleReCaptchaProvider>
    ) : (
      <SigninForm
        className={'signinForm'}
        email={email}
        setEmail={dispatchSetEmail}
        password={password}
        setPassword={setPassword}
        postSignin={dispatchSignin}
        isSigningIn={isLookingUp || isSigningIn}
        errorDetailMap={lookupErrorDetailMap || signinErrorDetailMap}
        topLevelError={signinFormTopLevelError()}
        didSessionExpireAutomatically={didSessionExpireAutomatically}
      />
    )

  // determine content to show
  let content
  if (isCspAuthenticated) {
    content = <Rdrct to={cspSigninUrl()} />
  } else if (showVerifyForm) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <ExtraVerificationPopup
          email={email}
          password={password}
          step={1}
          defaultWrongEmailText={dispatchSetUserStatus}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
        />
      </PageAndBoxContainer>
    )
  } else if (
    isExtraVerificationPopupOpen &&
    (showVerifyPhoneForm || userEmailVerified)
  ) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <ExtraVerificationPopup
          email={email}
          password={password}
          step={2}
          isVerifyingUser={verifyPhoneLoading}
          verifyTopLevelError={verifyPhoneTopLevelError}
          verificationResent={verificationPhoneResent}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
        />
      </PageAndBoxContainer>
    )
  } else if (mfaChallengeInitiated) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <VerifyForm
          id={'verifyCode'}
          email={email}
          password={password}
          title={<Trns source={'Verify Code'} />}
          subtitle={
            <>
              <p>
                <Trns
                  source={
                    'Enter the 6-digit verification code sent to ${yourEmailOrPhone}'
                  }
                  expressions={{
                    yourEmailOrPhone: (
                      <p
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        {
                          mfaDeliveryMedium === MfaDeliveryMedium.Email
                            ? mfaDestination
                            : '(•••) •••-' + mfaDestination?.slice(-4)
                          // ^ must manually format phone number since its not valid (format given is '+*******1234')
                        }
                      </p>
                    ),
                  }}
                />
              </p>
              <Trns
                source={
                  'Prefer ${otherMethod}? Update your code delivery method in Profile Settings.'
                }
                expressions={{
                  otherMethod: (
                    <Trns
                      source={
                        mfaDeliveryMedium === MfaDeliveryMedium.Email
                          ? MfaDeliveryMedium.SMS
                          : 'email'
                      }
                    />
                  ),
                }}
              />
            </>
          }
          buttonText={<Trns source={'Verify Code'} />}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
          setStatus={dispatchAbandonMfaChallenge}
          confirmCognitoSignin={dispatchConfirmCognitoSignin}
          postCognitoResendMfaCode={dispatchSignin}
          allowRememberMyDevice
        />
      </PageAndBoxContainer>
    )
  } else if (
    (userStatus !== UserStatus.NotVerified &&
      signinTopLevelError !== UserStatus.NotVerified) ||
    isSigningIn ||
    isLookingUp
  ) {
    if (showMarketingCMS && brand === SupportedBrands.Extend) {
      content = (
        <div css={style}>
          {/* UnauthedPageContainer and UnauthedBoxContainer used instead of PageAndBoxContainer
           * here of PageAndBoxContainer here because different styling is needed
           */}
          <UnauthedPageContainer
            css={halfPageStyling}
            topLeftImage={
              showVerifyForm && brandName !== SupportedBrands.Bmo ? (
                <div
                  css={css`
                    position: relative;
                    top: 30px;
                    left: 30px;
                  `}
                >
                  <BrandPrimaryLogo />
                </div>
              ) : undefined
            }
          >
            <UnauthedBoxContainer
              className={'unauthedBoxContainer'}
              hideBrandLogo={showVerifyForm}
              hideFooter={showVerifyForm || showVerifyPhoneForm}
              noBox={showVerifyForm || showVerifyPhoneForm}
              boxStyling={boxStyling}
            >
              {signInContent}
            </UnauthedBoxContainer>
          </UnauthedPageContainer>
          <SigninCMS />
        </div>
      )
    } else {
      content = (
        <PageAndBoxContainer
          showVerifyForm={showVerifyForm}
          brandName={brandName}
          showVerifyPhoneForm={showVerifyPhoneForm}
        >
          {signInContent}
        </PageAndBoxContainer>
      )
    }
  } else if (!mfaChallengeInitiated && !showVerifyForm) {
    content = (
      <PageAndBoxContainer
        showVerifyForm={showVerifyForm}
        brandName={brandName}
        showVerifyPhoneForm={showVerifyPhoneForm}
      >
        <VerifyForm
          id={'verifyEmail'}
          email={email}
          password={password}
          title={<Trns source={'Verify Email'} />}
          subtitle={
            <p>
              <Trns
                source={'Enter the verification code sent to ${destination}'}
                expressions={{
                  destination: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      {email}
                    </span>
                  ),
                }}
              />
            </p>
          }
          buttonText={<Trns source={'Verify Email'} />}
          isVerifyingUser={isVerifyingUser || isSigningIn}
          verifyTopLevelError={verifyEmailTopLevelError}
          resendVerificationTopLevelError={resendVerificationTopLevelError}
          verificationResent={verificationEmailResent}
          setStatus={dispatchSetUserStatus}
          postVerification={dispatchPostUserVerificationThenSignin}
          postResendVerification={dispatchPostUserResendVerification}
          acceptOrgInvite={acceptOrgInvite}
        />
      </PageAndBoxContainer>
    )
  }

  return (
    <>
      <Helmet>
        <title>{`Create virtual cards with the card in your pocket. | ${appName}`}</title>
        <meta
          name="description"
          content={`Create, send, and manage virtual cards for all your business payments and simplify expense processes with ${appName}.`}
        />
      </Helmet>
      {content}
    </>
  )
}

export default SigninPage
